import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useState } from "react";
import {
  estimateTaxReserve,
  priceTaxTargetNet,
  quoteTaxEstimate,
  type TaxEstimateRule,
  type TaxQuote,
  type TaxReserveEstimate,
} from "@/lib/api";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number) {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

/** Human echo for an exact whole-cent input, or null when not a whole number. */
function echoCents(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const cents = Number(trimmed);
  return Number.isSafeInteger(cents) ? money(cents) : null;
}

/** Human echo for an exact whole-basis-point input. */
function echoBp(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const bp = Number(trimmed);
  return Number.isSafeInteger(bp) ? `${(bp / 100).toFixed(2)}% (${bp} bp)` : null;
}

// The verified rule the quote endpoints require. Typed against the exported
// request contract (`TaxEstimateRule`) so the source object is no longer an
// ad-hoc literal; the generated source sub-type is not re-exported from
// lib/api, so it is checked structurally through this rule type.
function rule(input: {
  jurisdiction: string;
  rateBp: string;
  effectiveFrom: string;
  publisher: string;
  reference: string;
  url: string;
}): TaxEstimateRule {
  return {
    jurisdiction_code: input.jurisdiction.trim(),
    tax_type: "sales_tax",
    rate_basis_points: Number(input.rateBp),
    effective_from: input.effectiveFrom,
    source: {
      kind: "official_authority",
      publisher: input.publisher.trim(),
      reference: input.reference.trim(),
      url: input.url.trim(),
      retrieved_at: new Date().toISOString(),
    },
  };
}

export function TaxEstimatesTab() {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [jurisdiction, setJurisdiction] = useState("");
  const [asOf, setAsOf] = useState("");
  const [rateBp, setRateBp] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [publisher, setPublisher] = useState("");
  const [reference, setReference] = useState("");
  const [url, setUrl] = useState("");
  const [quote, setQuote] = useState<TaxQuote | null>(null);
  const [liability, setLiability] = useState("");
  const [completeThrough, setCompleteThrough] = useState("");
  const [sourceReference, setSourceReference] = useState("");
  const [complete, setComplete] = useState(false);
  const [manualBase, setManualBase] = useState("");
  const [manualBp, setManualBp] = useState("");
  const [ownerNote, setOwnerNote] = useState("");
  const [reserve, setReserve] = useState<TaxReserveEstimate | null>(null);
  const [reserveSource, setReserveSource] = useState<"books" | "manual" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const facts = () =>
    rule({
      jurisdiction,
      rateBp,
      effectiveFrom,
      publisher,
      reference,
      url,
    });

  const runQuote = async (event: FormEvent, targetNet: boolean) => {
    event.preventDefault();
    const amountCents = Number(amount);
    if (
      !Number.isInteger(amountCents) ||
      !jurisdiction.trim() ||
      !asOf ||
      !effectiveFrom ||
      !publisher.trim() ||
      !reference.trim() ||
      !url.trim() ||
      !Number.isInteger(Number(rateBp))
    ) {
      setError(
        "A quote needs a whole-cent amount (10000 = $100.00), a jurisdiction, both dates, a whole-basis-point rate (100 bp = 1%), and a verified source with publisher, reference, and URL.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setQuote(null);
    try {
      const input = {
        amount_cents: amountCents,
        currency_code: currency.trim().toUpperCase(),
        jurisdiction_code: jurisdiction.trim(),
        as_of: asOf,
        price_mode: targetNet ? ("included" as const) : ("added" as const),
        rules: [facts()],
      };
      setQuote(
        targetNet ? await priceTaxTargetNet(input) : await quoteTaxEstimate(input),
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const runBooks = async (event: FormEvent) => {
    event.preventDefault();
    const cents = Number(liability);
    if (!Number.isInteger(cents) || !completeThrough || !sourceReference.trim()) {
      setError(
        "A books reserve needs the liability in whole cents (3200 = $32.00), the period end, and a source reference.",
      );
      return;
    }
    if (!complete) {
      setError(
        "A books reserve is refused until the period is marked complete.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setReserve(null);
    setReserveSource(null);
    try {
      setReserve(
        await estimateTaxReserve({
          method: "books",
          currency_code: currency.trim().toUpperCase(),
          tax_liability_cents: cents,
          complete_through: completeThrough,
          source_reference: sourceReference.trim(),
          complete: true,
        }),
      );
      setReserveSource("books");
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const runManual = async (event: FormEvent) => {
    event.preventDefault();
    const cents = Number(manualBase);
    const bp = Number(manualBp);
    if (!Number.isInteger(cents) || !Number.isInteger(bp) || !ownerNote.trim()) {
      setError(
        "A manual reserve needs a whole-cent base (10000 = $100.00), a whole-basis-point rate (100 bp = 1%), and your note.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setReserve(null);
    setReserveSource(null);
    try {
      setReserve(
        await estimateTaxReserve({
          method: "manual",
          currency_code: currency.trim().toUpperCase(),
          taxable_base_cents: cents,
          rate_basis_points: bp,
          owner_note: ownerNote.trim(),
        }),
      );
      setReserveSource("manual");
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6">
      <p className="text-sm text-[var(--muted-foreground)]">
        These are three manual estimate models — not a filing. You supply the
        amount, rate, and verified source; Accounting does not guess a
        jurisdiction rate, file a return, or send a payment. Nothing here is
        stored as a filed return.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
      <form
        onSubmit={(event) => void runQuote(event, false)}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">Price with a verified rule</h3>
        <p className="text-xs text-[var(--muted-foreground)]">
          Manual model: the server calculates only from the rule you enter
          below, and refuses a jurisdiction it cannot support. Each quote
          records the publisher, reference, URL, and a retrieval timestamp
          taken at submit.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <Field
            id="tax-amount"
            label="Amount (cents)"
            value={amount}
            onChange={setAmount}
            hint="Exact whole cents sent to the server — 10000 = $100.00."
            echo={echoCents(amount)}
            echoTestId="tax-amount-human"
          />
          <Field id="tax-currency" label="Currency" value={currency} onChange={setCurrency} />
          <Field id="tax-jurisdiction" label="Jurisdiction" value={jurisdiction} onChange={setJurisdiction} />
          <DateField id="tax-as-of" label="As of" value={asOf} onChange={setAsOf} />
          <Field
            id="tax-rate-bp"
            label="Rate (basis points)"
            value={rateBp}
            onChange={setRateBp}
            hint="Exact whole basis points sent to the server — 100 bp = 1%."
            echo={echoBp(rateBp)}
            echoTestId="tax-rate-human"
          />
          <DateField id="tax-effective" label="Rule starts" value={effectiveFrom} onChange={setEffectiveFrom} />
          <Field id="tax-publisher" label="Publisher" value={publisher} onChange={setPublisher} />
          <Field id="tax-reference" label="Reference" value={reference} onChange={setReference} />
          <Field id="tax-url" label="Source URL" value={url} onChange={setUrl} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy}>
            Quote added tax
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={(event) => void runQuote(event, true)}
          >
            Price for target net
          </Button>
        </div>
        {quote ? (
          <p className="text-sm" data-testid="tax-quote-result">
            Estimate only, not a filing or a payment. Tax{" "}
            {money(quote.tax_amount_cents)}, customer total{" "}
            {money(quote.customer_total_cents)}, effective rate{" "}
            {(quote.effective_rate_basis_points / 100).toFixed(2)}% (
            {quote.effective_rate_basis_points} bp), from{" "}
            {quote.applied_rules[0]?.source.reference}.
          </p>
        ) : null}
      </form>
      <form
        onSubmit={(event) => void runBooks(event)}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">Reserve from complete books</h3>
        <p className="text-xs text-[var(--muted-foreground)]">
          Manual model: the liability figure comes from you, and the server
          accepts it only once the period is marked complete here.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <Field
            id="reserve-liability"
            label="Tax liability (cents)"
            value={liability}
            onChange={setLiability}
            hint="Exact whole cents — 3200 = $32.00."
            echo={echoCents(liability)}
            echoTestId="reserve-liability-human"
          />
          <DateField id="reserve-through" label="Complete through" value={completeThrough} onChange={setCompleteThrough} />
          <Field id="reserve-source" label="Source reference" value={sourceReference} onChange={setSourceReference} />
          <label className="flex items-center gap-2 text-sm" htmlFor="reserve-complete">
            <input
              id="reserve-complete"
              type="checkbox"
              checked={complete}
              onChange={(event) => setComplete(event.target.checked)}
            />
            Period is complete
          </label>
        </div>
        <Button type="submit" disabled={busy}>
          Estimate books reserve
        </Button>
        {reserve && reserveSource === "books" ? (
          <p className="text-sm" data-testid="tax-reserve-books-result">
            Estimate only, not a filing or a payment. Books reserve{" "}
            {money(reserve.amount_cents)}
            {reserve.method.kind === "books_derived"
              ? `, complete through ${reserve.method.complete_through}.`
              : "."}
          </p>
        ) : null}
      </form>
      <form
        onSubmit={(event) => void runManual(event)}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">Manual percentage reserve</h3>
        <p className="text-xs text-[var(--muted-foreground)]">
          Manual model: your own base multiplied by your own percentage. The
          note records why you chose it.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <Field
            id="manual-base"
            label="Taxable base (cents)"
            value={manualBase}
            onChange={setManualBase}
            hint="Exact whole cents — 10000 = $100.00."
            echo={echoCents(manualBase)}
            echoTestId="manual-base-human"
          />
          <Field
            id="manual-bp"
            label="Rate (basis points)"
            value={manualBp}
            onChange={setManualBp}
            hint="Exact whole basis points — 100 bp = 1%."
            echo={echoBp(manualBp)}
            echoTestId="manual-bp-human"
          />
          <Field id="manual-note" label="Your note" value={ownerNote} onChange={setOwnerNote} />
        </div>
        <Button type="submit" variant="secondary" disabled={busy}>
          Estimate manual reserve
        </Button>
        {reserve && reserveSource === "manual" ? (
          <p className="text-sm" data-testid="tax-reserve-manual-result">
            Estimate only, not a filing or a payment. Manual reserve{" "}
            {money(reserve.amount_cents)}
            {reserve.method.kind === "manual_percentage"
              ? ` on ${money(reserve.method.applied_to_minor)} at ${(reserve.method.rate_basis_points / 100).toFixed(2)}% (${reserve.method.rate_basis_points} bp).`
              : "."}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  hint,
  echo,
  echoTestId,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  echo?: string | null;
  echoTestId?: string;
}) {
  return (
    <label className="grid gap-1 text-xs font-medium" htmlFor={id}>
      {label}
      <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} />
      {hint ? (
        <span className="max-w-56 font-normal text-[var(--muted-foreground)]">
          {hint}
        </span>
      ) : null}
      {echo ? (
        <output
          className="font-normal text-[var(--muted-foreground)]"
          data-testid={echoTestId}
        >
          = {echo}
        </output>
      ) : null}
    </label>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-xs font-medium" htmlFor={id}>
      {label}
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
